import { Fragment, useState, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  StopIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { RetellVoice } from '../types';

interface VoiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (voice: RetellVoice) => void;
  voices: RetellVoice[];
  selectedVoiceId?: string;
  isLoading?: boolean;
}

// Map accents/voice names to languages
function getVoiceLanguage(voice: RetellVoice): string {
  const accent = voice.accent?.toLowerCase() || '';
  const name = voice.voice_name?.toLowerCase() || '';

  // Check voice name for explicit language mentions
  if (name.includes('spanish') || name.includes('español')) return 'Spanish';
  if (name.includes('portuguese') || name.includes('portugues') || name.includes('português')) return 'Portuguese';
  if (name.includes('french') || name.includes('français')) return 'French';
  if (name.includes('german') || name.includes('deutsch')) return 'German';
  if (name.includes('italian') || name.includes('italiano')) return 'Italian';
  if (name.includes('japanese') || name.includes('日本語')) return 'Japanese';
  if (name.includes('korean') || name.includes('한국어')) return 'Korean';
  if (name.includes('chinese') || name.includes('mandarin') || name.includes('中文')) return 'Chinese';
  if (name.includes('hindi') || name.includes('हिंदी')) return 'Hindi';
  if (name.includes('arabic') || name.includes('عربي')) return 'Arabic';

  // Map accents to languages
  if (accent.includes('american') || accent.includes('british') || accent.includes('australian') ||
      accent.includes('irish') || accent.includes('scottish') || accent.includes('english')) {
    return 'English';
  }
  if (accent.includes('spanish') || accent.includes('mexican') || accent.includes('latin')) {
    return 'Spanish';
  }
  if (accent.includes('brazilian') || accent.includes('portuguese')) {
    return 'Portuguese';
  }
  if (accent.includes('french')) return 'French';
  if (accent.includes('german')) return 'German';
  if (accent.includes('italian')) return 'Italian';

  // Default to English for most voices without specific accent
  return 'English';
}

// Get unique genders from voices
function getUniqueGenders(voices: RetellVoice[]): string[] {
  const genders = new Set<string>();
  voices.forEach((voice) => {
    if (voice.gender) {
      genders.add(voice.gender);
    }
  });
  return Array.from(genders).sort();
}

// Get unique accents from voices
function getUniqueAccents(voices: RetellVoice[]): string[] {
  const accents = new Set<string>();
  voices.forEach((voice) => {
    if (voice.accent) {
      accents.add(voice.accent);
    }
  });
  return Array.from(accents).sort();
}

// Get unique languages from voices
function getUniqueLanguages(voices: RetellVoice[]): string[] {
  const languages = new Set<string>();
  voices.forEach((voice) => {
    languages.add(getVoiceLanguage(voice));
  });
  return Array.from(languages).sort();
}

// Stock voice providers from Retell - filter out custom/cloned voices
const STOCK_VOICE_PROVIDERS = [
  'elevenlabs',
  '11labs',
  'openai',
  'azure',
  'cartesia',
  'playht',
  'deepgram',
  'retell',
];

function isStockVoice(voice: RetellVoice): boolean {
  const provider = voice.provider?.toLowerCase() || '';
  return STOCK_VOICE_PROVIDERS.some(p => provider.includes(p));
}

export default function VoiceSelectionModal({
  isOpen,
  onClose,
  onSelect,
  voices,
  selectedVoiceId,
  isLoading = false,
}: VoiceSelectionModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [selectedGender, setSelectedGender] = useState<string | null>(null);
  const [selectedAccent, setSelectedAccent] = useState<string | null>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Filter to only stock voices (exclude custom/cloned voices)
  const stockVoices = voices.filter(isStockVoice);

  const genders = getUniqueGenders(stockVoices);
  const accents = getUniqueAccents(stockVoices);
  const languages = getUniqueLanguages(stockVoices);

  // Filter voices based on search and filters (using stockVoices which already excludes custom voices)
  const filteredVoices = stockVoices.filter((voice) => {
    // Language filter
    if (selectedLanguage && getVoiceLanguage(voice) !== selectedLanguage) {
      return false;
    }
    // Gender filter
    if (selectedGender && voice.gender !== selectedGender) {
      return false;
    }
    // Accent filter
    if (selectedAccent && voice.accent !== selectedAccent) {
      return false;
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        voice.voice_name.toLowerCase().includes(query) ||
        voice.voice_id.toLowerCase().includes(query) ||
        voice.provider?.toLowerCase().includes(query) ||
        voice.accent?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Stop audio when modal closes
  useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
    }
  }, [isOpen]);

  // Play voice preview
  const handlePlayVoice = (voice: RetellVoice) => {
    if (!voice.preview_audio_url) return;

    // If already playing this voice, stop it
    if (playingVoiceId === voice.voice_id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Play new audio
    const audio = new Audio(voice.preview_audio_url);
    audioRef.current = audio;
    setPlayingVoiceId(voice.voice_id);

    audio.play().catch((err) => {
      console.error('Error playing audio:', err);
      setPlayingVoiceId(null);
    });

    audio.onended = () => {
      setPlayingVoiceId(null);
      audioRef.current = null;
    };

    audio.onerror = () => {
      setPlayingVoiceId(null);
      audioRef.current = null;
    };
  };

  // Handle voice selection
  const handleSelectVoice = (voice: RetellVoice) => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
    }
    onSelect(voice);
    onClose();
  };

  // Reset filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedLanguage(null);
    setSelectedGender(null);
    setSelectedAccent(null);
  };

  const hasActiveFilters = searchQuery || selectedLanguage || selectedGender || selectedAccent;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all w-full max-w-4xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                      Select Voice
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Search and Filters */}
                  <div className="mt-4 space-y-3">
                    {/* Search */}
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search voices..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Filter Pills */}
                    <div className="flex flex-wrap gap-2">
                      {/* Language Filter */}
                      <select
                        value={selectedLanguage || ''}
                        onChange={(e) => setSelectedLanguage(e.target.value || null)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">All Languages</option>
                        {languages.map((language) => (
                          <option key={language} value={language}>
                            {language}
                          </option>
                        ))}
                      </select>

                      {/* Gender Filter */}
                      <select
                        value={selectedGender || ''}
                        onChange={(e) => setSelectedGender(e.target.value || null)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">All Genders</option>
                        {genders.map((gender) => (
                          <option key={gender} value={gender}>
                            {gender}
                          </option>
                        ))}
                      </select>

                      {/* Accent Filter */}
                      <select
                        value={selectedAccent || ''}
                        onChange={(e) => setSelectedAccent(e.target.value || null)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-full bg-white hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">All Accents</option>
                        {accents.map((accent) => (
                          <option key={accent} value={accent}>
                            {accent}
                          </option>
                        ))}
                      </select>

                      {/* Clear Filters */}
                      {hasActiveFilters && (
                        <button
                          onClick={handleClearFilters}
                          className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Voice List */}
                <div className="flex-1 overflow-y-auto p-6">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : filteredVoices.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No voices found matching your criteria.</p>
                      {hasActiveFilters && (
                        <button
                          onClick={handleClearFilters}
                          className="mt-2 text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filteredVoices.map((voice) => {
                        const isSelected = selectedVoiceId === voice.voice_id;
                        const isPlaying = playingVoiceId === voice.voice_id;

                        return (
                          <div
                            key={voice.voice_id}
                            className={`relative flex items-center gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                              isSelected
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectVoice(voice)}
                          >
                            {/* Play Button */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayVoice(voice);
                              }}
                              disabled={!voice.preview_audio_url}
                              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                voice.preview_audio_url
                                  ? isPlaying
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                              }`}
                            >
                              {isPlaying ? (
                                <StopIcon className="h-5 w-5" />
                              ) : (
                                <PlayIcon className="h-5 w-5 ml-0.5" />
                              )}
                            </button>

                            {/* Voice Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 truncate">
                                  {voice.voice_name}
                                </span>
                                {isSelected && (
                                  <CheckIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-500">
                                <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-medium">
                                  {getVoiceLanguage(voice)}
                                </span>
                                {voice.gender && (
                                  <span className="text-xs">• {voice.gender}</span>
                                )}
                                {voice.accent && (
                                  <span className="text-xs">• {voice.accent}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {filteredVoices.length} voice{filteredVoices.length !== 1 ? 's' : ''} available
                    </span>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
